<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="subscription-content">
      <!-- 方案已內含 ChatMate：計費走主訂閱，不顯示加購 -->
      <div v-if="planIncludesChatMate" class="info-section plan-includes-section">
        <h2>ChatMate 已包含在您的方案中</h2>
        <p class="plan-includes-lead">
          目前方案<span v-if="planName">「{{ planName }}」</span>已包含 ChatMate AI 客服模組，無需另行加購。
          續費、信用卡與扣款週期請至「訂閱與交易記錄」管理。
        </p>
        <router-link to="/client/subscription" class="btn btn-primary btn-lg">
          <i class="ri-file-list-3-line"></i>
          前往訂閱與交易記錄
        </router-link>
      </div>

      <template v-else>
      <!-- 訂閱狀態（加購） -->
      <div class="info-section">
        <h2>ChatMate 訂閱狀態</h2>
        <div v-if="addonSubscription" class="subscription-status">
          <div class="status-item">
            <span class="status-label">訂閱狀態：</span>
            <span :class="getStatusBadge(addonSubscription.status)">{{ getStatusText(addonSubscription.status) }}</span>
          </div>
          <div class="status-item">
            <span class="status-label">計費週期：</span>
            <span class="status-value">{{ addonSubscription.billing_cycle === 'yearly' ? '年費' : '月費' }}</span>
          </div>
          <div class="status-item">
            <span class="status-label">每期金額：</span>
            <span class="status-value">NT$ {{ formatCurrency(addonSubscription.amount) }}</span>
          </div>
          <div v-if="addonSubscription.start_date" class="status-item">
            <span class="status-label">開始日期：</span>
            <span class="status-value">{{ formatDate(addonSubscription.start_date) }}</span>
          </div>
          <div v-if="addonSubscription.next_billing_date" class="status-item">
            <span class="status-label">下次扣款日：</span>
            <span class="status-value">{{ formatDate(addonSubscription.next_billing_date) }}</span>
          </div>
          <div class="status-item">
            <span class="status-label">自動續訂：</span>
            <span class="status-value">{{ addonSubscription.auto_renew ? '是' : '否' }}</span>
          </div>

          <!-- 信用卡記錄 -->
          <div class="status-item" v-if="paymentMethod">
            <span class="status-label">付款信用卡：</span>
            <span class="status-value">
              {{ paymentMethod.card_type || '信用卡' }} **** **** **** {{ paymentMethod.card_last4 || '—' }}
              <template v-if="!paymentMethod.card_last4">（綠界未回傳末四碼）</template>
            </span>
          </div>
        </div>

        <div v-else class="empty-state">
          <i class="ri-robot-line"></i>
          <p>尚未訂閱 ChatMate 服務</p>
        </div>
      </div>

      <!-- 啟用 ChatMate（尚未訂閱） -->
      <div v-if="!addonSubscription || addonSubscription.status === 'cancelled'" class="info-section">
        <h2>啟用 ChatMate</h2>
        <div class="activation-box">
          <p>ChatMate 是 AI 智能客服模組，讓您的客戶能與 AI 助理即時對話。</p>

          <div class="billing-cycle-toggle">
            <button :class="['btn-toggle', billingCycle === 'monthly' ? 'active' : '']" @click="billingCycle = 'monthly'">
              月費 NT$ 400 / 月
            </button>
            <button :class="['btn-toggle', billingCycle === 'yearly' ? 'active' : '']" @click="billingCycle = 'yearly'">
              年費 NT$ 4,800 / 年（省 2 個月）
            </button>
          </div>

          <button class="btn btn-primary btn-lg" :disabled="activating" @click="handleActivate">
            <i class="ri-bank-card-line"></i>
            <span v-if="activating">處理中...</span>
            <span v-else>前往綠界付款啟用</span>
          </button>
          <p class="activate-hint">點擊後將跳轉至綠界付款頁面，輸入信用卡後完成首次扣款即可啟用</p>
        </div>
      </div>

      <!-- 待付款狀態 -->
      <div v-if="addonSubscription && addonSubscription.status === 'pending'" class="info-section">
        <h2>等待付款確認</h2>
        <div class="pending-box">
          <i class="ri-time-line"></i>
          <p>您的訂閱訂單已建立，等待付款確認中。</p>
          <p class="hint">如已在綠界頁面完成付款，請稍候片刻，系統將自動更新狀態。</p>
          <div class="pending-actions">
            <button class="btn btn-secondary" @click="fetchData">重新整理狀態</button>
            <button class="btn btn-warning-outline" :disabled="cancelling" @click="handleCancelPendingAndRetry">
              <i class="ri-refresh-line"></i>
              <span v-if="cancelling">處理中...</span>
              <span v-else>取消，重新付款</span>
            </button>
            <button v-if="isDev" class="btn btn-dev" :disabled="devActivating" @click="handleDevActivate">
              <i class="ri-bug-line"></i>
              <span v-if="devActivating">處理中...</span>
              <span v-else>模擬付款成功（本機測試）</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 取消訂閱（已啟用） -->
      <div v-if="addonSubscription && addonSubscription.status === 'active'" class="info-section">
        <h2>管理訂閱</h2>
        <div class="manage-section">
          <p class="manage-description">取消後將停止自動扣款，現有訂閱期限內仍可使用服務。</p>
          <button class="btn btn-danger-outline" :disabled="cancelling" @click="handleCancel">
            <span v-if="cancelling">處理中...</span>
            <span v-else>取消 ChatMate 訂閱</span>
          </button>
        </div>
      </div>
      </template>
    </div>

    <!-- ECPay 付款表單（隱藏，自動提交） -->
    <form ref="ecpayForm" :action="ecpayPaymentUrl" method="POST" target="_self" style="display:none">
      <input v-for="(value, key) in ecpayOrderData" :key="key" type="hidden" :name="key" :value="value" />
    </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';
import apiClient from '@/api/axios.js';

export default {
  name: 'ChatMateSubscription',
  setup() {
    const { pageTitle } = usePageTitle('ChatMate 訂閱管理');
    const loading = ref(false);
    const error = ref(null);

    const addonSubscription = ref(null);
    const planIncludesChatMate = ref(false);
    const planName = ref(null);
    const paymentMethod = ref(null);
    const billingCycle = ref('monthly');
    const activating = ref(false);
    const cancelling = ref(false);

    const isDev = process.env.NODE_ENV !== 'production';
    const devActivating = ref(false);

    const ecpayForm = ref(null);
    const ecpayPaymentUrl = ref('');
    const ecpayOrderData = reactive({});

    const fetchData = async () => {
      loading.value = true;
      error.value = null;

      try {
        const [addonRes, pmRes] = await Promise.allSettled([
          apiClient.get('/client/addon-subscriptions'),
          apiClient.get('/client/payment-methods')
        ]);

        if (addonRes.status === 'fulfilled' && addonRes.value?.success && addonRes.value?.data) {
          const data = addonRes.value.data;
          planIncludesChatMate.value = !!data.planIncludesChatMate;
          planName.value = data.planName || null;
          const subs = data.addonSubscriptions || [];
          // 取最新的 chatmate 訂閱
          addonSubscription.value = subs.find(s =>
            s.addon_type === 'chatmate' && ['pending', 'active'].includes(s.status)
          ) || subs.find(s => s.addon_type === 'chatmate') || null;
        } else {
          planIncludesChatMate.value = false;
          planName.value = null;
        }

        if (pmRes.status === 'fulfilled' && pmRes.value?.success && pmRes.value?.data) {
          const methods = pmRes.value.data.paymentMethods || [];
          paymentMethod.value = methods.find(pm => pm.subscription_type === 'addon') || null;
        }
      } catch (err) {
        console.error('獲取訂閱狀態失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取訂閱狀態失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleActivate = async () => {
      activating.value = true;
      try {
        const res = await apiClient.post(`/client/addon-subscriptions/chatmate/initiate`, {
          billingCycle: billingCycle.value
        });

        if (res?.success && res?.data) {
          const { paymentUrl, orderData, addonName, amount } = res.data;

          ecpayPaymentUrl.value = paymentUrl;
          Object.keys(ecpayOrderData).forEach(k => delete ecpayOrderData[k]);
          Object.assign(ecpayOrderData, orderData);

          await showSuccess(
            '準備付款',
            `即將跳轉至綠界付款頁面\n${addonName} NT$ ${amount.toLocaleString()} / ${billingCycle.value === 'yearly' ? '年' : '月'}`
          );

          setTimeout(() => {
            ecpayForm.value?.submit();
          }, 300);
        } else {
          await showError('啟用失敗', res?.message || '請稍後再試');
        }
      } catch (err) {
        console.error('啟用 ChatMate 失敗:', err);
        const msg = err.response?.data?.message || err.message || '啟用失敗，請稍後再試';
        await showError('啟用失敗', msg);
      } finally {
        activating.value = false;
      }
    };

    const handleCancelPendingAndRetry = async () => {
      const confirmed = await showConfirm('取消並重新付款', '將取消目前的待付款訂單，讓您重新選擇方案並付款。確定嗎？');
      if (!confirmed) return;

      cancelling.value = true;
      try {
        await apiClient.post(`/client/addon-subscriptions/${addonSubscription.value.id}/cancel`, {});
        addonSubscription.value = null;
        await fetchData();
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      } finally {
        cancelling.value = false;
      }
    };

    const handleDevActivate = async () => {
      devActivating.value = true;
      try {
        await apiClient.post(`/client/addon-subscriptions/${addonSubscription.value.id}/dev-activate`);
        await showSuccess('[DEV] 模擬成功', '已模擬付款成功，訂閱已啟用');
        await fetchData();
      } catch (err) {
        await showError('失敗', err.response?.data?.message || err.message);
      } finally {
        devActivating.value = false;
      }
    };

    const handleCancel = async () => {
      const confirmed = await showConfirm('確認取消', '確定要取消 ChatMate 訂閱嗎？取消後將不再自動扣款。');
      if (!confirmed) return;

      cancelling.value = true;
      try {
        await apiClient.post(`/client/addon-subscriptions/${addonSubscription.value.id}/cancel`, {});
        await showSuccess('已取消', 'ChatMate 訂閱已取消，現有期限內仍可使用服務');
        await fetchData();
      } catch (err) {
        await showError('取消失敗', err.response?.data?.message || err.message);
      } finally {
        cancelling.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const getStatusBadge = (status) => {
      const map = {
        pending: 'badge badge-warning',
        active: 'badge badge-success',
        cancelled: 'badge badge-secondary',
        expired: 'badge badge-danger'
      };
      return map[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const map = {
        pending: '待付款',
        active: '訂閱中',
        cancelled: '已取消',
        expired: '已過期'
      };
      return map[status] || status || '未知';
    };

    onMounted(() => {
      fetchData();
    });

    return {
      pageTitle,
      loading,
      error,
      addonSubscription,
      planIncludesChatMate,
      planName,
      paymentMethod,
      billingCycle,
      activating,
      cancelling,
      ecpayForm,
      ecpayPaymentUrl,
      ecpayOrderData,
      isDev,
      devActivating,
      fetchData,
      handleActivate,
      handleDevActivate,
      handleCancelPendingAndRetry,
      handleCancel,
      formatDate,
      formatCurrency,
      getStatusBadge,
      getStatusText
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.subscription-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.plan-includes-section {
  border: 1px solid #c8e6c9;
  background: #f1f8f4;
}

.plan-includes-lead {
  color: #333;
  line-height: 1.65;
  margin-bottom: 20px;
}

.plan-includes-section .btn {
  text-decoration: none;
}

.info-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.info-section h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.subscription-status {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-label {
  font-weight: 600;
  color: #666;
  min-width: 120px;
}

.status-value {
  color: #333;
}

/* 啟用區塊 */
.activation-box {
  padding: 8px 0;
}

.activation-box p {
  color: #555;
  margin-bottom: 20px;
  line-height: 1.6;
}

.billing-cycle-toggle {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
}

.btn-toggle {
  padding: 10px 24px;
  border: none;
  background: white;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.btn-toggle.active {
  background: #1976d2;
  color: white;
  font-weight: 600;
}

.activate-hint {
  font-size: 13px;
  color: #888;
  margin-top: 12px;
}

/* 等待付款 */
.pending-box {
  text-align: center;
  padding: 20px;
  background: #fff8e1;
  border: 1px solid #ffc107;
  border-radius: 8px;
}

.pending-box i {
  font-size: 40px;
  color: #f57c00;
  display: block;
  margin-bottom: 12px;
}

.pending-box p {
  color: #555;
  margin-bottom: 8px;
}

.pending-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.btn-warning-outline {
  background: transparent;
  color: #e65100;
  border: 1px solid #e65100;
}

.btn-warning-outline:hover:not(:disabled) {
  background: #e65100;
  color: white;
}

.btn-dev {
  background: #6a1b9a;
  color: white;
  border: none;
}

.btn-dev:hover:not(:disabled) {
  background: #4a148c;
}

/* 管理訂閱 */
.manage-section {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.manage-description {
  color: #666;
  font-size: 14px;
  margin: 0;
}

/* 空狀態 */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.hint {
  font-size: 13px;
  color: #aaa;
  margin-top: 8px;
}

/* 按鈕 */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-lg {
  padding: 12px 28px;
  font-size: 15px;
}

.btn-primary {
  background: #1976d2;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #1565c0;
}

.btn-secondary {
  background: #757575;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #616161;
}

.btn-danger-outline {
  background: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
}

.btn-danger-outline:hover:not(:disabled) {
  background: #dc3545;
  color: white;
}

/* 徽章 */
.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success { background: #e8f5e9; color: #2e7d32; }
.badge-warning { background: #fff8e1; color: #f57c00; }
.badge-danger { background: #ffebee; color: #c62828; }
.badge-secondary { background: #f5f5f5; color: #666; }

/* 載入/錯誤 */
.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1976d2;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
